import { useMemo } from "react";
import useApi from "../useApi";

const BASE = "/suppliers";

export const useSuppliersList = (params = {}) => {
  const queryParams = useMemo(() => {
    const q = {};
    if (params.status) q.status = params.status;
    if (params.type) q.type = params.type;
    if (params.country) q.country = params.country;
    if (params.vatRole) q.vatRole = params.vatRole;
    if (params.search) q.search = params.search;
    return q;
  }, [params.status, params.type, params.country, params.vatRole, params.search]);

  const api = useApi(BASE, {
    method: "GET",
    params: queryParams,
    immediate: true,
    errorMessage: "Failed to fetch suppliers",
    transformResponse: (res) => (Array.isArray(res?.data) ? res.data : []),
  });

  const refetch = async () => {
    try {
      const res = await api.execute();
      return { success: true, data: res };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || "Failed to fetch suppliers" };
    }
  };

  return {
    data: api.data ?? [],
    loading: api.loading,
    error: api.error,
    refetch,
  };
};

export const useCreateSupplier = () => useApi(BASE, { method: "POST" });

export const useUpdateSupplier = () => {
  const api = useApi(
    (id) => `${BASE}/${id}`,
    { method: "PUT", errorMessage: "Failed to update supplier" }
  );

  const execute = async (id, payload) => {
    if (!id) throw new Error("Supplier id is required");
    const res = await api.execute(id, payload);
    return res?.data ?? res;
  };

  return { execute, loading: api.loading, error: api.error };
};

export const useUpdateSupplierServices = () => {
  const api = useApi(
    (id) => `${BASE}/${id}/services`,
    { method: "POST", errorMessage: "Failed to update supplier services" }
  );

  const execute = async (id, services) => {
    if (!id) throw new Error("Supplier id is required");
    return api.execute(id, { services });
  };

  return { execute, loading: api.loading, error: api.error };
};

export const useDeleteSupplier = () => {
  const api = useApi(
    (id) => `${BASE}/${id}`,
    { method: "DELETE", errorMessage: "Failed to delete supplier" }
  );

  const execute = async (id) => {
    if (!id) throw new Error("Supplier id is required");
    return api.execute(id);
  };

  return { execute, loading: api.loading, error: api.error };
};

export const useExportSuppliers = () => {
  const api = useApi(BASE + "/export", {
    method: "POST",
    params: (format) => ({ format: format ?? "csv" }),
    responseType: "blob",
    errorMessage: "Failed to export suppliers",
  });

  const execute = async (format = "csv") => {
    return api.execute(format);
  };

  return { execute, loading: api.loading, error: api.error };
};

export const useSupplierHealth = (params = {}) => {
  const queryParams = useMemo(() => {
    const q = {};
    if (params.status) q.status = params.status;
    if (params.search) q.search = params.search;
    return q;
  }, [params.status, params.search]);

  const api = useApi(`${BASE}/health`, {
    method: "GET",
    params: queryParams,
    immediate: true,
    errorMessage: "Failed to fetch supplier health",
    transformResponse: (res) => (Array.isArray(res?.data) ? res.data : []),
  });

  const refetch = async () => {
    try {
      const res = await api.execute();
      return { success: true, data: res };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || "Failed to fetch supplier health" };
    }
  };

  return {
    data: api.data ?? [],
    loading: api.loading,
    error: api.error,
    refetch,
  };
};

// API for fetching supplier hotels from Natecnia
export const useSupplierHotels = (params = {}) => {
  const queryParams = useMemo(() => {
    const q = {};
    if (params.regionId) q.regionId = params.regionId;
    if (params.supplierId) q.supplierId = params.supplierId;
    if (params.search) q.search = params.search;
    if (params.page) q.page = params.page;
    if (params.limit) q.limit = params.limit;
    return q;
  }, [params.regionId, params.supplierId, params.search, params.page, params.limit]);

  const api = useApi(`${BASE}/hotels`, {
    method: "GET",
    params: queryParams,
    immediate: true,
    errorMessage: "Failed to fetch supplier hotels",
    transformResponse: (res) => (Array.isArray(res?.data) ? res.data : []),
  });

  const refetch = async () => {
    try {
      const res = await api.execute();
      return { success: true, data: res };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || "Failed to fetch supplier hotels" };
    }
  };

  return {
    data: api.data ?? [],
    loading: api.loading,
    error: api.error,
    refetch,
  };
};
